<template>
	<section id="profile" v-if="selectedUser" :class="{'teammate': tab == 'team', 'teammate-disabled': selectedUser.isDisabled}">
		<template v-if="addingUser">
			<editActionBar
				:isEditActive="true" 
				confirmText="Create &amp; Activate" 
				:confirmAction="onCreateAndActivate" 
				:updateAction="onUpdateProfile" 
				:cancelAction="onCancel" 
				:deleteAllowed="false" 
				:isDisabled="false" 
			/>
		</template>
		<template v-else>
			<editActionBar
				v-if="tab === 'team'"
				:isEditActive="update"
				:updateText="'Update ' + selectedUser.name"
				:confirmAction="onSave" 
				:updateAction="onUpdateProfile" 
				:cancelAction="onCancel" 
				:deleteAllowed="true" 
				:deleteMessage="'Are you sure you want to delete ' + selectedUser.name + '?'" 
				:deleteExecuteText="'Yes. Delete ' + selectedUser.name" 
				:deleteAction="onDeleteSelected"
				enableText="Enable User" 
				:enableAction="onSetDisable" 
				:disableAction="onSetDisable" 
				:isDisabled="selectedUser.isDisabled" 
			/>
			<editActionBar
				v-else
				:isEditActive="update"
				updateText="Update My Profile"
				:confirmAction="onSave" 
				:updateAction="onUpdateProfile" 
				:cancelAction="onCancel" 
				:deleteAllowed="false"
				:isDisabled="false" 
			/>
		</template>

		<v-form id="profile-info" class="profile-info" :key="update" v-model="isProfileValid">
			<div class="user-info">
				<fieldset class="update-user-info">
					<legend>
						<h3 class="line-header">User Information</h3>
					</legend>
					<!-- We're not using photos for initial release... maybe later
					<img src="@/assets/imgs/head_shot.jpg" alt="head-shot" class="profile-head-shot" />
					-->
					<v-text-field
						v-model="selectedUser.name"
						label="Name"
						:readonly="!update && !addingUser"
						:outlined="update || addingUser"
						placeholder="ex. Alex Smith"
						:rules="rules"
					/>
					<v-text-field
						v-model="selectedUser.emailAddress"
						label="Email"
						:readonly="true && !addingUser"
						:outlined="false || addingUser"
						placeholder="ex. asmith@example.com"
						:rules="emailRules"
					/>
				</fieldset>
			</div>

			<div class="access-info">
				<fieldset class="update-access-info">
					<legend>
						<h3 class="line-header">Access Information</h3>
					</legend>
					<div>
						<template v-if="activeTenantId === 'master' && !addingUser">
							<v-text-field
								v-model="selectedUser.tenantId"
								id="selected-user-tenant"
								label="Tenant ID"
								:readonly="true"
								:outlined="false"
							/>
						</template>
						<label for="selected-user-role">
							Role
							<infoDialogBox
								title="What Are Roles?"
								activatorText="what's this?"
							>
								<template>
									<p>Roles determine which functions and pages the user can access.</p>
									<ul>
										<li>Administrators have access to all ARIA functions.</li>
										<li>User admins can only add and manage non-admin users.</li>
										<li>Configuration admins can only create and modify profiles.</li>
										<li>Deployment admins have full access to the configuration tools.</li>
										<li>Read only users can view all non-sensistive data but cannot change anything.</li>
									</ul>
								</template>
							</infoDialogBox>
						</label>
						<v-select
							id="selected-user-role"
							v-model="selectedUser.accessRole"
							:items="roles"
							item-value="text"
							:readonly="!update && !addingUser"
							:outlined="update || addingUser"
							:rules="rules"
						></v-select>
					</div>
					<div v-if="hasClientOptions">
						<label>Client Access</label>
						<template v-if="selectedUser.isAllowedAllClients || update">
							<v-checkbox
								id="selected-user-access"
								v-model="selectedUser.isAllowedAllClients"
								:readonly="!update"
								label="Can access all clients"
								class="client-access-all"
							></v-checkbox>
						</template>
						<template v-if="!selectedUser.isAllowedAllClients">
							<v-autocomplete
								id="selected-user-access"
								v-model="selectedUserClients"
								:items="clients"
								placeholder="select desired clients"
								multiple
								small-chips
								hide-selected
								deletable-chips
								solo
								:rules="rules"
								:readonly="!update"
							></v-autocomplete>
						</template>
					</div>
				</fieldset>
			</div>
		</v-form>
	</section>
	<div v-else>
		<div class="profile-action-bar">
			<v-skeleton-loader type="button" style="margin: 0.5em 1em"></v-skeleton-loader>
		</div>
		<div class="profile-info">
			<div class="user-info">
				<v-skeleton-loader type="heading" style="margin-bottom: 1em"></v-skeleton-loader>
				<v-skeleton-loader type="text" style="margin-bottom: 1em"></v-skeleton-loader>
				<v-skeleton-loader type="text"></v-skeleton-loader>
			</div>
			<div class="access-info">
				<v-skeleton-loader type="heading" style="margin-bottom: 1em"></v-skeleton-loader>
				<v-skeleton-loader type="text" style="margin-bottom: 1em"></v-skeleton-loader>
				<v-skeleton-loader type="chip"></v-skeleton-loader>
			</div>
		</div>
	</div>
</template>

<script>
	/**
	 * @fileoverview profile.vue displays a user's profile. Profiles can be modified.
	 * It relies on vuex to map the getters and state variables from the store files.
	 * 
	 * @components
	 * 		editActionBar - action bar used to modify a profile
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapGetters, mapState } from "vuex";
	import editActionBar from '@/components/edit-action-bar.vue';

	export default {
		name: "profile",
		props: {
			selectedUserId: String,	// userId being displayed
			roles: Array,	// roles available
			clients: Array,	// clients available
			tab: String,	// selected tab
			/**
			 * Back to Team function
			 */
			backToTeam: {
				Type: Function,
				Required: false,
			},
		},
		data() {
			return {
				showDialog: false,	// show delete dialog
				update: false,	// if true, edit in progress
				tempUserBackup: {},	// temporary user in case of cancel
				isProfileValid: false,	// validation flag
				/**
				 * Validation rules
				 */
				rules: [
					value => !!value || 'Required',
					value => (value || '').length > 0 || 'Required',
				],
				/**
				 * Email validation rules
				 */
				emailRules: [
					value => !!value || 'Required',
					value => (value || '').length > 0 || 'Required',
					value => {
						const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
						return pattern.test(value) || "Invalid e-mail."
					}
				]
			};
		},
		components: {
			editActionBar
		},
		computed: {
			...mapGetters({
				getTeamMember: "getUserById",
			}),
			...mapState({
				activeTenantId: (state) => state.account.activeTenantId,
				loggedInUserId: (state) => state.account.loggedInUserId,
				loggedInUser: (state) => state.account.loggedInUser,
			}),
			/**
			 * Getter and setter for the selected user
			 */
			selectedUser: {
				get() {
					if (this.selectedUserId === this.loggedInUserId) {
						return this.loggedInUser;
					} else if (this.selectedUserId === '') {
						return {
							userId: '',
							name: '',
							emailAddress: '',
							accessRole: 'FullAdministrator',
							isAllowedAllClients: false,
							allowedClients: [],
							tenantId: this.activeTenantId,
							isDisabled: false
						}
					}
					else {
						return this.getTeamMember(this.selectedUserId);
					}
				},
				set(user) {
					// setting items directly triggers reactive display
					this.selectedUser.name = user.name;
					this.selectedUser.emailAddress = user.emailAddress;
					this.selectedUser.accessRole = user.accessRole;
					this.selectedUser.isAllowedAllClients = user.isAllowedAllClients;
					this.selectedUser.allowedClients = user.allowedClients;
				},
			},
			/**
			 * The user has client options if they have more than one client
			 * @returns true if the user has more than one client
			 */
			hasClientOptions() {
				return this.clients.length > 1;
			},
			/**
			 * Getter and setter for the selected user clients
			 */
			selectedUserClients: {
				get() {
					// flatten object list from source
					let existingClientList = this.selectedUser.allowedClients ? this.selectedUser.allowedClients : [];
					let clientList = existingClientList.map((item) => {
						return item.clientId;
					});
					return clientList;
				},
				set(list) {
					// build object list for source
					let that = this;
					list.forEach( function(client, index){
						that.selectedUser.allowedClients[index] = {
							"clientId": client,
						};
					});
				}
			},
			/**
			 * Getter for addingUser flag
			 * @returns true if the end-user is adding a new user
			 */
			addingUser: { 
				get() {
					if (this.selectedUserId === '') {
						return true;
					}
					return false;
				}
			}
		},
		methods: {
			/**
			 * Update profile selected. Set the temp user.
			 */
			onUpdateProfile() {
				this.update = true;
 
				// create a deep copy of the user for easy restoration
				this.tempUserBackup = this._.cloneDeep(this.selectedUser);
			},
			/**
			 * Adding user button
			 */
			async onCreateAndActivate() {
				if (this.isProfileValid) {
					this.selectedUser.tenantId = this.activeTenantId;

					if (!this.hasClientOptions) {
						this.selectedUser.isAllowedAllClients = true;
					}
					await this.$store.dispatch("saveUser", this.selectedUser);
					await this.backToTeam();
				} else {
					const notification = {
						status: "error",
						message: "Fix missing or invalid entries."
					};
					this.$store.dispatch("notifications/addNotification", notification);
				}
			},
			/**
			 * Toggle isDisabled for the selected user. Save profile.
			 */
			onSetDisable() {
				this.selectedUser.isDisabled = !this.selectedUser.isDisabled;
				this.onSave();
			},
			/**
			 * Save user profile
			 */
			onSave() {
				if (this.isProfileValid) {
					var that = this;
					this.update = false;

					var editedUser = {
						tenantId: that.tenantId ? that.tenantId : that.activeTenantId,
						userId: that.selectedUser.userId,
						name: that.selectedUser.name,
						emailAddress: that.selectedUser.emailAddress,
						accessRole: that.selectedUser.accessRole,
						isAllowedAllClients: that.selectedUser.isAllowedAllClients,
						isDisabled: that.selectedUser.isDisabled,
					};

					if (that.selectedUser.isAllowedAllClients == false ) {
						editedUser.allowedClients = that.selectedUser.allowedClients;
					}

					this.$store.dispatch("saveUser", editedUser);
				} else {
					const notification = {
						status: "error",
						message: "Fix missing or invalid entries."
					};
					this.$store.dispatch("notifications/addNotification", notification);
				}
			},
			/**
			 * Cancel button. Restore selected user to the temp user.
			 */
			onCancel() {
				this.update = false;
				if (this.addingUser) {
					this.backToTeam();
				}

				// restore selectedUser from backup... then cleanup
				this.selectedUser = this.tempUserBackup;
				this.tempUserBackup = {};
			},
			/**
			 * Actually delete the user. Navigate back to the team.
			 */
			onDeleteSelected() {
				this.showDialog = false;

				var payload = {
					userId: this.selectedUser.userId,
					tenantId: this.activeTenantId
				};

				this.$store.dispatch("deleteUser",  payload);
				this.backToTeam();
			},
			/**
			 * Toggle showDialog
			 */
			toggleDialog() {
				this.showDialog = !this.showDialog;
			},
		},
	};
</script>

<!-- Styles defined in _profile.scss -->